import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useApi } from '../lib/ApiProvider';
import type { CurrentUser } from '../lib/api-types';
import type { LoginInput } from '../lib/api';

/** Broadcast by the API client when a silent refresh fails (session expired). */
export const SESSION_EXPIRED_EVENT = 'derliz:session-expired';

interface AuthContextValue {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const api = useApi();
  const [user, setUser] = useState<CurrentUser | null>(null);

  const login = useCallback(
    async (input: LoginInput) => {
      await api.login(input);
      const me = await api.me();
      setUser(me);
    },
    [api],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
    }
  }, [api]);

  // When the API client fails to silently refresh, drop the in-memory user so the
  // protected routes bounce back to /login.
  useEffect(() => {
    const handler = () => {
      api.clearSession();
      setUser(null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, [api]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: user !== null, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}
